/**
 * L3: Search tab (01-references §6 S5). It copies the 1Password iOS search screen: a field at the
 * top, results grouped into items, tags, wallets and categories (01 §2.1), and `=` for filters.
 * It blends in Quick Search: the `w:` wallet token with completion, and a scope pill that writes
 * the same token (one variable, two surfaces, D-6/D-8).
 * The input carries data-keep so the shell keeps focus and caret across re-renders.
 */
import { CATEGORY_LABEL, type AppActions, type AppState } from "../contract.js";
import { accountButton, banner, cell, emptyState, fakeChip, group, navBar, sheetFrame } from "../shell/chrome.js";
import { avatar, h, icon } from "../shell/dom.js";
import { renderQuery, walletCompletions, parseQuery } from "../search/query.js";
import { searchResults, walletCounts } from "../search/rank.js";
import { isFavorite } from "../vm/items.js";
import { itemRow } from "./row.js";

export function renderSearch(state: AppState, actions: AppActions): HTMLElement {
  const snap = state.snapshot!;
  const order = snap.wallets.map((w) => w.id);
  const r = searchResults(state.searchText, snap);
  const selected = r.selectedWallets;
  const pillLabel = selected.length === 0 ? "All" : selected.length === 1 ? snap.wallets.find((w) => w.id === selected[0])!.name : `${selected.length} wallets`;

  const input = h("input", {
    type: "search",
    placeholder: "Search Vault",
    "aria-label": "Search",
    autocomplete: "off",
    autocapitalize: "off",
    spellcheck: "false",
    enterkeyhint: "search",
    value: state.searchText,
    "data-keep": "search",
    oninput: (e: Event) => actions.setSearchText((e.target as HTMLInputElement).value),
  });
  input.value = state.searchText;

  const header = navBar({ left: accountButton(state, actions), largeTitle: "Search", largeTitleAccessory: snap.fixture ? fakeChip() : null });
  const bar = h(
    "div",
    { class: "search-wrap" },
    h("label", { class: "search-field" }, icon("search", "icon small"), input),
    h(
      "button",
      { class: selected.length ? "scope-pill active" : "scope-pill", "aria-label": `Search in: ${pillLabel}`, onclick: () => actions.openSheet({ kind: "search-scope" }) },
      icon("wallet", "icon small"),
      pillLabel,
    ),
  );

  const tokenChips =
    selected.length && r.completing === null
      ? h(
          "div",
          { class: "chips" },
          ...selected.map((id) =>
            h(
              "button",
              { class: "chip token", "aria-label": `Remove wallet ${id}`, onclick: () => actions.toggleSearchWallet(id) },
              snap.wallets.find((w) => w.id === id)!.name,
              icon("close", "icon small"),
            ),
          ),
        )
      : null;

  const content: (Node | null)[] = [];
  if (r.filters) {
    content.push(
      group(
        "Filters",
        r.filters.map((f) =>
          f.kind === "wallet-token"
            ? cell({ label: "Wallet", lead: icon("wallet"), detail: "w:", onClick: () => actions.setSearchText("w:") })
            : cell({ label: f.label, lead: icon(f.list === "favorites" ? "star" : "items"), chevron: true, onClick: () => actions.navigate({ name: "items", list: f.list }) }),
        ),
        { small: true },
      ),
    );
  } else if (r.completing) {
    const parsed = parseQuery(state.searchText, order);
    content.push(
      group(
        "Wallets",
        r.completing.options.length
          ? r.completing.options.map((o) =>
              cell({
                label: o.wallet.name,
                lead: avatar(o.wallet.name),
                detail: String(o.count),
                // Choosing every wallet writes no token at all (D-8: all wallets is not a filter).
                onClick: () => actions.setSearchText(renderQuery([...parsed.wallets, o.wallet.id], "", order)),
              }),
            )
          : [cell({ label: `No wallet starts with “${r.completing.fragment}”`, cls: "disabled" })],
        { small: true },
      ),
    );
  } else if (r.unknownWallets.length) {
    content.push(banner("error", `No wallet named “${r.unknownWallets.join(", ")}”`, `Wallets: ${snap.wallets.map((w) => w.id).join(", ")}.`));
  } else if (r.idle) {
    const counts = walletCounts(snap);
    content.push(
      h("p", { class: "hint" }, "Search titles, tags and wallets. Type ", h("code", {}, "w:work"), " to search one wallet, or ", h("code", {}, "="), " for filters."),
      group(
        "Wallets",
        snap.wallets.map((w) => cell({ label: w.name, lead: avatar(w.name), detail: String(counts.get(w.id) ?? 0), onClick: () => actions.setSearchText(`w:${w.id} `) })),
        { small: true },
      ),
      group(
        "Categories",
        Object.entries(CATEGORY_LABEL).map(([id, label]) => cell({ label, lead: icon("items"), chevron: true, onClick: () => actions.navigate({ name: "items", list: id as keyof typeof CATEGORY_LABEL }) })),
        { small: true },
      ),
    );
  } else {
    if (r.items.length) {
      content.push(
        h("div", { class: "result-group-title" }, "Items"),
        h("div", { class: "list" }, ...r.items.map((hit) => itemRow(hit.item, snap.wallets, actions, { showWallet: true, favorite: isFavorite(hit.item, state.favoriteOverrides) }))),
      );
    }
    if (r.tags.length) {
      // A tag tap searches for the tag's value (the ranker scores tag values) and keeps the
      // wallet token, the way a Wallets tap keeps the query.
      content.push(
        h("div", { class: "result-group-title" }, "Tags"),
        group(
          null,
          r.tags.map((t) =>
            cell({ label: `${t.key}: ${t.value}`, lead: icon("tag"), detail: String(t.count), onClick: () => actions.setSearchText(renderQuery(selected, t.value, order)) }),
          ),
        ),
      );
    }
    if (r.wallets.length) {
      content.push(
        h("div", { class: "result-group-title" }, "Wallets"),
        group(null, r.wallets.map((w) => cell({ label: w.wallet.name, lead: avatar(w.wallet.name), detail: String(w.count), onClick: () => actions.setSearchText(`w:${w.wallet.id} `) }))),
      );
    }
    if (r.categories.length) {
      content.push(
        h("div", { class: "result-group-title" }, "Categories"),
        group(null, r.categories.map((c) => cell({ label: CATEGORY_LABEL[c.category], lead: icon("items"), detail: String(c.count), chevron: true, onClick: () => actions.navigate({ name: "items", list: c.category }) }))),
      );
    }
    if (!r.items.length && !r.tags.length && !r.wallets.length && !r.categories.length) {
      content.push(emptyState("search", "No results", r.query ? `Nothing matches “${r.query}”.` : "Nothing in these wallets."));
    }
  }

  return h("main", { class: "screen", "data-screen": "search" }, header, bar, tokenChips, ...content);
}

/**
 * The scope pill's multi-select sheet. Each tick rewrites the `w:` token in the field live while
 * the sheet is open (D-8), so the pill teaches the keyboard shortcut.
 */
export function renderSearchScopeSheet(state: AppState, actions: AppActions): HTMLElement {
  const snap = state.snapshot!;
  const order = snap.wallets.map((w) => w.id);
  const parsed = parseQuery(state.searchText, order);
  const counts = walletCounts(snap);
  const selected = new Set(parsed.wallets);
  return sheetFrame(
    "Search In",
    actions,
    h("p", { class: "sheet-note" }, "Ticking a wallet writes it into the search field as ", h("code", {}, "w:"), " text, so you can type it next time."),
    group(
      null,
      walletCompletions("", order, []).map((id) =>
        cell({ label: snap.wallets.find((w) => w.id === id)!.name, lead: avatar(snap.wallets.find((w) => w.id === id)!.name), detail: String(counts.get(id) ?? 0), check: selected.has(id), onClick: () => actions.toggleSearchWallet(id) }),
      ),
      { foot: state.searchText ? `Search field: ${state.searchText}` : "Search field is empty." },
    ),
  );
}
